<template>
    <div>
        <BoCustomerType
            class="ppv2-flex ppv2-justify-center ppv2-items-center"
            :customer="object_customer"
            @customer-type-changed="handleCustomerTypeChange"
        />

        <!-- Pro customer fields -->
        <div
            v-show="hasProCustomer"
            class="ppv2-w-full ppv2-grid ppv2-grid-cols-2 ppv2-mt-2 ppv2-mb-2"
            :style="{ gap: normalizeSize(16, true) }"
        >
            <div>
                <BaseInput
                    v-model="object_customer.social_reason"
                    :label="$t('form_customer_v2.main_information.company_name.label')"
                    :placeholder="$t('form_customer_v2.main_information.company_name.placeholder')"
                    required
                />
            </div>
            <div>
                <BaseInput
                    v-model="object_customer.siret"
                    :label="$t('form_customer_v2.main_information.siret.label')"
                    :placeholder="$t('form_customer_v2.main_information.siret.placeholder')"
                    required
                />
            </div>
        </div>

        <!-- Common user fields -->
        <div
            class="ppv2-w-full ppv2-grid ppv2-grid-cols-2"
            :style="{
                gap: normalizeSize(16, true),
                marginTop: normalizeSizeVertical(20, true),
                marginBottom: normalizeSizeVertical(20, true),
            }"
        >
            <div>
                <BaseInput
                    v-model="object_user.last_name"
                    ref="last_name"
                    :error="errors.last_name"
                    :state="errors.last_name ? 'error' : 'default'"
                    :label="$t('form_customer_v2.main_information.last_name.label')"
                    :placeholder="$t('form_customer_v2.main_information.last_name.placeholder')"
                    required
                />
                <small class="text-danger" v-if="errors.last_name">{{ errors.last_name }}</small>
            </div>
            <div>
                <BaseInput
                    v-model="object_user.first_name"
                    ref="first_name"
                    :error="errors.first_name"
                    :state="errors.first_name ? 'error' : 'default'"
                    :label="$t('form_customer_v2.main_information.first_name.label')"
                    :placeholder="$t('form_customer_v2.main_information.first_name.placeholder')"
                    required
                />
                <small class="text-danger" v-if="errors.first_name">{{ errors.first_name }}</small>
            </div>
        </div>

        <!-- More conditional and shared fields -->
        <div
            class="ppv2-w-full ppv2-grid ppv2-grid-cols-2"
            :style="{ gap: normalizeSize(16, true), marginTop: normalizeSize(20, true), marginBottom: normalizeSize(20, true) }"
        >
            <div v-show="hasProCustomer">
                <BaseInput
                    v-model="object_customer.fonction"
                    :label="$t('form_customer_v2.main_information.function.label')"
                    :placeholder="$t('form_customer_v2.main_information.function.placeholder')"
                    required
                />
            </div>

            <div v-show="hasGeneralCustomer">
                <BaseInput
                    v-model="object_customer.civility"
                    :label="$t('form_customer_v2.main_information.civility.label')"
                    :placeholder="$t('form_customer_v2.main_information.civility.placeholder')"
                    required
                />
            </div>

            <BaseInput
                v-model="object_user.email"
                :label="$t('form_customer_v2.main_information.email.label')"
                :placeholder="$t('form_customer_v2.main_information.email.placeholder')"
                required
            />
        </div>

        <div
            class="ppv2-w-full ppv2-grid ppv2-grid-cols-2"
            :style="{
                gap: normalizeSize(16, true),
                marginTop: normalizeSizeVertical(20, true),
                marginBottom: normalizeSizeVertical(20, true),
            }"
        >
            <BaseInput
                v-model="object_user.phone_number"
                type="number"
                :label="$t('form_customer_v2.main_information.phone.label')"
                :placeholder="$t('form_customer_v2.main_information.phone.placeholder')"
                required
            />

            <div v-show="hasProCustomer">
                <BaseInput
                    v-model="object_customer.phone_fixed"
                    type="number"
                    :label="$t('form_customer_v2.main_information.fix_phone.label')"
                    :placeholder="$t('form_customer_v2.main_information.fix_phone.placeholder')"
                    required
                />
            </div>

            <div v-show="hasGeneralCustomer">
                <BaseSelect
                    v-model="object_customer.customer_group"
                    :label="$t('form_customer_v2.main_information.customer_group.label')"
                    :options="array_customerGroup"
                    required
                />
            </div>
        </div>

        <div
            class="ppv2-w-full ppv2-grid ppv2-grid-cols-2"
            :style="{
                gap: normalizeSize(16, true),
                marginTop: normalizeSizeVertical(20, true),
                marginBottom: normalizeSizeVertical(20, true),
            }"
        >
            <div v-show="hasProCustomer">
                <BaseSelect
                    v-model="object_customer.customer_group"
                    :label="$t('form_customer_v2.main_information.customer_group.label')"
                    :options="array_customerGroup"
                    required
                />
            </div>

            <div>
                <BaseSelect
                    v-model="object_customer.origin_id"
                    :label="$t('form_customer_v2.main_information.origin.label')"
                    :options="array_origin"
                    required
                />
            </div>

            <div v-show="hasGeneralCustomer">
                <BaseDate
                    v-model="object_customer.birthdate"
                    :label="$t('form_customer_v2.main_information.date_of_birth.label')"
                    required
                />
            </div>
        </div>

        <div
            class="ppv2-w-full ppv2-grid ppv2-grid-cols-2"
            :style="{
                gap: normalizeSize(16, true),
                marginTop: normalizeSizeVertical(20, true),
                marginBottom: normalizeSizeVertical(20, true),
            }"
        >
            <div v-show="hasGeneralCustomer">
                <BaseSelect
                    v-model="object_user.gender"
                    :label="$t('form_customer_v2.main_information.gender.label')"
                    :options="array_gender"
                    required
                />
            </div>
        </div>

        <BoCustomerAutorisationsRgbd
            :customer="object_customer"
            :user="user"
            :lastRgpd="object_lastRbgd"
            :hasEdit="hasEdit"
            ref="rgbd"
        />
    </div>
</template>

<script>
import BoCustomerType from './BoCustomerType.vue'
import BoCustomerAutorisationsRgbd from './BoCustomerAutorisationsRgbd.vue'
import BaseInput from '../../Shared/BaseInput.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseDate from '../../Shared/BaseDate.vue'
import normalizeSizeVertical from '../../../utils/normalizeSizeVertical'
import normalizeSize from '../../../utils/normalizeSize'
export default {
    components: {
        BoCustomerType,
        BoCustomerAutorisationsRgbd,
        BaseInput,
        BaseSelect,
        BaseDate,
    },
    props: ['customer', 'user', 'customerGroup', 'gender', 'origins', 'lastRgpd', 'hasEdit'],
    data() {
        const defaultCustomer = {
            social_reason: '',
            siret: '',
            fonction: '',
            civility: '',
            phone_fixed: '',
            customer_group: '',
            origin_id: '',
            birthdate: '',
            user_id: 0,
            customer_type: '1',
        }

        const defaultUser = {
            last_name: '',
            first_name: '',
            email: '',
            phone_number: '',
            gender: '',
            user_id: 0,
        }

        return {
            errors: {
                first_name: '',
                last_name: '',
            },
            hasProCustomer: true,
            hasGeneralCustomer: false,
            array_origin: [],
            array_customerGroup: [],
            array_gender: [],
            selected_origin: 0,
            object_customer: !this.hasEdit
                ? { ...defaultCustomer }
                : JSON.parse(JSON.stringify(this.customer)) || { ...defaultCustomer },
            object_user: !this.hasEdit ? { ...defaultUser } : JSON.parse(JSON.stringify(this.user)) || { ...defaultUser },
            object_lastRbgd: JSON.parse(JSON.stringify(this.lastRgpd)),
        }
    },
    methods: {
        normalizeSizeVertical,
        normalizeSize,
        handleCustomerTypeChange(event) {
            const isGeneral = event.customer_type == 1
            this.hasProCustomer = !isGeneral
            this.hasGeneralCustomer = isGeneral
        },
        formatDateForInput(dateString) {
            const date = new Date(dateString)
            const yyyy = date.getFullYear()
            const mm = String(date.getMonth() + 1).padStart(2, '0')
            const dd = String(date.getDate()).padStart(2, '0')
            return `${yyyy}-${mm}-${dd}`
        },
        reset() {
            this.object_customer = {
                social_reason: '',
                siret: '',
                fonction: '',
                civility: '',
                phone_fixed: '',
                customer_group: '',
                origin_id: '',
                birthdate: '',
                user_id: 0,
                customer_type: '1',
            }
            this.object_user = {
                last_name: '',
                first_name: '',
                email: '',
                phone_number: '',
                gender: '',
                user_id: 0,
            }
        },
        validate() {
            this.errors.first_name = ''
            this.errors.last_name = ''
            let valid = true

            if (!this.object_user.first_name) {
                this.errors.first_name = this.$t('form_customer_v2.main_information.first_name.required')
                this.scrollToError('first_name')
                valid = false
            }

            if (!this.object_user.last_name) {
                this.errors.last_name = this.$t('form_customer_v2.main_information.last_name.required')
                this.scrollToError('last_name')
                valid = false
            }

            return valid
        },

        scrollToError(refName) {
            const el = this.$refs[refName]?.$el || this.$refs[refName]
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                })
            }
        },
        buildSelectOptions(source) {
            return source.map((item) => ({
                value: item.id ?? item,
                label: item.name ?? item,
            }))
        },
    },
    mounted() {
        const isGeneral = this.object_customer.customer_type == 1
        this.hasProCustomer = !isGeneral
        this.hasGeneralCustomer = isGeneral

        if (this.object_customer.birthdate) {
            this.object_customer.birthdate = this.formatDateForInput(this.object_customer.birthdate)
        }

        this.array_origin = this.buildSelectOptions(this.origins)
        this.array_customerGroup = this.buildSelectOptions(this.customerGroup)
        this.array_gender = this.buildSelectOptions(this.gender)
    },
}
</script>
